import { useMemo, useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { useAtomValue } from "jotai";
import { Icon } from "@iconify/react";
import { StoryItem } from "./StoryItem";
import { activeLabelsState } from "./JotaiAtoms";
import { StyledTooltip } from "./themeConfig";

type LabelDistributionProps = {
  stories: StoryItem[];
  minPercentage?: number;
  tableRef?: React.RefObject<HTMLElement>; // Reference to the table to measure cells
};

type LabelCount = {
  label: string;
  count: number;
  percentage: number;
  color: string;
};

// Simple hash function for consistent color generation
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

// Convert HSL to hex color
function hslToHex(h: number, s: number, l: number): string {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Generate color from label string using algorithmic approach
function getLabelColor(label: string, isDark: boolean): string {
  const hash = hashString(label);

  // Use hash to generate hue (0-360) for maximum color variety
  const hue = hash % 360;

  // Different saturation and lightness for light vs dark mode
  if (isDark) {
    // Dark mode: muted, darker colors
    // Saturation: 35-50% for subtle but visible colors
    const saturation = 35 + (hash % 16);
    // Lightness: 20-30% for darker backgrounds
    const lightness = 20 + ((hash >> 8) % 11);
    return hslToHex(hue, saturation, lightness);
  } else {
    // Light mode: softer, lighter colors
    // Saturation: 40-60% for gentle pastel colors
    const saturation = 40 + (hash % 21);
    // Lightness: 75-88% for light, readable backgrounds
    const lightness = 75 + ((hash >> 8) % 14);
    return hslToHex(hue, saturation, lightness);
  }
}

function getTextColor(isDark: boolean): string {
  return isDark ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)";
}

export function LabelBar({
  stories,
  minPercentage = 5,
  tableRef,
}: LabelDistributionProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const activeLabels = useAtomValue(activeLabelsState);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [cellGeometry, setCellGeometry] = useState<{
    left: number;
    width: number;
  } | null>(null);

  const getLabelDisplayName = (label: string): string => {
    if (activeLabels && activeLabels[label]) {
      const labelData = activeLabels[label];
      const display = labelData.display;

      // If display is a string, use it
      if (typeof display === "string") {
        return display || label;
      }

      // For icon labels or labels without display, use name if available
      if (labelData.name) {
        return labelData.name;
      }
    }
    return label;
  };

  const isIconLabel = (label: string): boolean => {
    return undefined !== getIconLabel(label);
  };

  const getIconLabel = (label: string): string | undefined => {
    if (activeLabels && activeLabels[label]) {
      const display = activeLabels[label].display;
      if (typeof display === "object") {
        return display.icon;
      }
    }
    return undefined;
  };

  useEffect(() => {
    const updateGeometry = () => {
      // Find the second cell (content cell) in the first table row
      const table = tableRef?.current || document.querySelector("table");
      if (!table) return;

      const firstRow = table.querySelector("tbody tr");
      if (!firstRow) return;

      const cells = firstRow.querySelectorAll("td");
      if (cells.length < 2) return;

      const secondCell = cells[1]; // Second cell (content cell)
      const rect = secondCell.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();

      setCellGeometry({
        left: rect.left - tableRect.left,
        width: rect.width,
      });
      setContainerWidth(rect.width);
    };

    updateGeometry();

    const resizeObserver = new ResizeObserver(updateGeometry);
    const table = tableRef?.current || document.querySelector("table");
    if (table) {
      resizeObserver.observe(table);
    }

    return () => resizeObserver.disconnect();
  }, [tableRef]);

  const distribution = useMemo(() => {
    // Count stories per label
    const labelCounts = new Map<string, number>();
    const totalStories = stories.length;

    for (const story of stories) {
      if (story.st_labels) {
        for (const label of story.st_labels) {
          labelCounts.set(label, (labelCounts.get(label) || 0) + 1);
        }
      }
    }

    if (labelCounts.size === 0 || totalStories === 0) return null;

    // Use measured width, or default to reasonable width if not yet measured
    const effectiveWidth = containerWidth > 0 ? containerWidth : 800;

    // Convert to array and calculate percentages
    const labelData: LabelCount[] = Array.from(labelCounts.entries()).map(
      ([label, count]) => ({
        label,
        count,
        percentage: (count / totalStories) * 100,
        color: getLabelColor(label, isDark),
      })
    );

    // Sort by count descending
    labelData.sort((a, b) => b.count - a.count);

    // Minimum width for a label to be readable (in pixels)
    const minReadableWidth = 40;

    // Build result by adding labels one by one until they become unreadable
    const result: LabelCount[] = [];

    for (let i = 0; i < labelData.length; i++) {
      const candidate = [...result, labelData[i]];

      // Calculate normalized percentages for this candidate set
      const totalPercentage = candidate.reduce(
        (sum, item) => sum + item.percentage,
        0
      );
      const normalized = candidate.map((item) => ({
        ...item,
        percentage: (item.percentage / totalPercentage) * 100,
      }));

      // Check if all labels would be readable with these percentages
      const allReadable = normalized.every((item) => {
        const widthInPixels = (item.percentage / 100) * effectiveWidth;
        return widthInPixels >= minReadableWidth;
      });

      if (allReadable) {
        // All labels are readable, keep this set
        result.push(labelData[i]);
      } else {
        // Adding this label makes something unreadable, stop here
        break;
      }
    }

    if (result.length === 0) return null;

    // Final normalization
    const totalShownPercentage = result.reduce(
      (sum, item) => sum + item.percentage,
      0
    );
    const normalized = result.map((item) => ({
      ...item,
      percentage: (item.percentage / totalShownPercentage) * 100,
    }));

    return normalized;
  }, [stories, isDark, containerWidth]);

  if (!distribution || distribution.length === 0) {
    return null;
  }

  if (!cellGeometry) {
    // Geometry not yet measured, don't render
    return null;
  }

  const textColor = getTextColor(isDark);

  return (
    <Box
      sx={{
        mt: 2,
        mb: 2,
        width: "100%",
        position: "relative",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          height: "16px",
          borderRadius: "6px",
          overflow: "visible",
          width: `${cellGeometry.width}px`,
          ml: `${cellGeometry.left}px`,
        }}
      >
        {distribution.map((item, index) => {
          const sectionWidth = (item.percentage / 100) * cellGeometry.width;
          const displayName = getLabelDisplayName(item.label);
          // Rough estimate: average character is ~7px at 11px font, plus padding
          const estimatedTextWidth = displayName.length * 7 + 8;
          const showText =
            !isIconLabel(item.label) && sectionWidth >= estimatedTextWidth;
          const isFirst = index === 0;
          const isLast = index === distribution.length - 1;

          return (
            <StyledTooltip
              key={item.label}
              title={
                isIconLabel(item.label) ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Icon
                      icon={getIconLabel(item.label)!}
                      style={{ fontSize: "14px" }}
                    />
                    <span>
                      {item.count} stories ({item.percentage.toFixed(1)}%)
                    </span>
                  </Box>
                ) : (
                  `${displayName}: ${
                    item.count
                  } stories (${item.percentage.toFixed(1)}%)`
                )
              }
              arrow
              enterDelay={300}
            >
              <Box
                sx={{
                  backgroundColor: item.color,
                  width: `${item.percentage}%`,
                  cursor: "default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 0.3,
                  fontSize: "11px",
                  lineHeight: "16px",
                  fontWeight: 500,
                  color: textColor,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  position: "relative",
                  transition: "filter 0.15s, box-shadow 0.15s, z-index 0s",
                  ...(isFirst && {
                    borderTopLeftRadius: "5px",
                    borderBottomLeftRadius: "5px",
                  }),
                  ...(isLast && {
                    borderTopRightRadius: "5px",
                    borderBottomRightRadius: "5px",
                  }),
                  "&:hover": {
                    filter: isDark ? "brightness(1.25)" : "brightness(0.85)",
                    boxShadow: isDark
                      ? "0 0 12px rgba(138, 180, 248, 0.35)"
                      : "none",
                    zIndex: 1,
                  },
                }}
              >
                {isIconLabel(item.label) ? (
                  <Icon
                    icon={getIconLabel(item.label)!}
                    style={{ fontSize: "11px" }}
                  />
                ) : (
                  showText && displayName
                )}
              </Box>
            </StyledTooltip>
          );
        })}
      </Box>
    </Box>
  );
}
